/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { Alert, Box, CircularProgress, Container, Divider, Grid, Paper, Typography } from '@mui/material';
import SelectField from '../../../components/ui/SelectField';
import InputField from '../../../components/ui/InputField';
import EventTypeField from '../CreateBooking/components/EventTypeField';
import { useIsInvoiceIssuingTenant } from '../../../hooks/useIsInvoiceIssuingTenant';
import PriceAdjustmentReasonField from '../../../components/PriceAdjustmentReasonField';
import DateRangeField from '../../../components/ui/DateRangeField';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { PricePreviewArgs } from '../../../api/booking/pricePreviewApiTypes';
import { usePricePreview } from '../../../hooks/usePricePreview';
import BookingCustomerClassSelect from '../CreateBooking/components/BookingCustomerClassSelect';
import { useEditBookingCustomerClass } from './hooks/useEditBookingCustomerClass';
import { useDispatch } from 'react-redux';
import {
    clearBookingCreationStates,
    clearEditCheckOutDate,
    setCity,
    setEditAddress,
    setEditBookingDetails,
    setEditCheckInDate,
    setEditCheckOutDate,
    setEditComment,
    setEditCompany,
    setEditCountry,
    setEditEmail,
    setEditEmailNote,
    setEditFirstName,
    setEditInvoiceNumber,
    setEditLastName,
    setEditNumberOfGuests,
    setEditPersoCity,
    setEditPhone,
    setEditZipCode,
    setEndTime,
    setEventType,
    setRevertEditBookingReservation,
    setSelectedEditApartment,
    setSelectedEditApartmentDetails,
    setSomeoneElseEditEmail,
    setSomeoneElseEditFirstName,
    setSomeoneElseEditLastName,
    setSomeoneElseEditPhone,
    setStartTime,
} from '../../../store/slices/bookingCreationSlice';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
    STATION_TIME_ZONE,
    formatAtStation,
    pickedInstantOrNow,
    stationCalendarDay,
    stationDateTime,
    stationInstant,
    stationTimeOfDay,
    zoneOfLocation,
} from '../../../utils/stationTime';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import { useCountryOptions } from '../../../hooks/useCountryOptions';
import { Trans, useTranslation } from 'react-i18next';
import { EditRoomRentalBookingPayload, PersonalDataValidation } from '../../../types/bookingCreation';
import { useNavigate, useParams } from 'react-router';
import StatusBadge from '../../../components/StatusBadge';
import Logger from '../../../common/logger';
import CustomButton from '../../../components/CustomButton';
import CustomerMailBlockedNote from '../../../components/booking/CustomerMailBlockedNote';
import { customerMailRefusalOf, savedOutcomeToast } from '../../../utils/customerMailGuard';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import { applyContactPrefill } from '../../../utils/organizationPrefill';
import { attributionPayloadForEdit } from '../../../utils/costCenter';
import VenueReservationSummary from '../../../components/VenueReservationSummary';
import { useGetBookingDetailsQuery } from '../../../api/booking/bookingApiSlice';
import {
    extractAddress,
    formatDateForDisplay,
    isEditBookingValid,
} from '../../../utils';
import { usePriceOverrideGuard } from '../../../hooks/usePriceOverrideGuard';
import CustomSeparator from '../../../components/CustomBreadCrumb';
import { makeRentalBookingDetailsUrl, paths } from '../../../routes';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { useFetchApartments } from '../../../flows/fetchApartmentsFlow';
import { useGuestCapacityGuard } from '../../../hooks/useGuestCapacityGuard';
import { AdditionalService as AdditionalServiceType } from '../../../types/bookingCreation';
import { useEditServiceRemapOnAssetSwitch } from './hooks/useEditServiceRemapOnAssetSwitch';
import { useFetchApartmentDetails } from '../../../flows/fetchApartmentDetailsFlow';
import { editFailureMessageKey, useEditRoomRentalBooking } from '../../../flows/editRoomRentalBookingFlow';
import SnackbarComponent from '../../common/SnackbarComponent';
import i18n from 'i18next';
import CustomModal from '../../common/CustomModal';
import { BOOKING_EMAIL_NOTE_MAX_LENGTH, getAvailableBookingTimes, sortByLocaleName } from '../../../utils';
import { useEventTypeOptions } from '../../../hooks/useEventTypeOptions';
import TimePickerComponent from '../../common/TimePicker';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import BookingEditDetailsAdditionalServices from './components/AdditionalServices/BookingEditDetailsAdditionalServices';
import BookingDetailsAdditionalServices from '../CreateBooking/components/AdditionalServices/BookingDetailsAdditionalServices';
import { coveredByOf, editPaymentBadgeStatus } from '../../../utils/bookingCoverage';
import { roomNounOf } from '../../../utils/useCaseLabels';
import { commentLabelKey } from '../../../utils/bookingCommentLabel';
import { saveAndSendCopyKeys, toApprovalState } from '../../../utils/bookingApproval';

dayjs.extend(isSameOrBefore);

/* Window keys have minute granularity — the time pickers have no seconds. */

/**
 * A stored window edge as `date + HH:mm` on the station's clock, the time
 * falling back to the instant's own. Same key as `stationDateTime(day, time)`
 * formatted with `stationWindowKey`, so an untouched window compares equal in
 * any browser zone.
 */
const windowKey = (
    date: Dayjs | string | null | undefined,
    time?: string | null,
    timeZone: string = STATION_TIME_ZONE,
): string => {
    if (!date) return '';
    const day = stationCalendarDay(dayjs(date).toDate(), timeZone);
    // Normalised to HH:mm, so '9:00' or '10:00:00' from the API cannot read as a change.
    const hhmm = time
        ? stationTimeOfDay(stationInstant(day, time, timeZone), timeZone)
        : stationTimeOfDay(date, timeZone);
    return `${day} ${hhmm}`;
};

/** The key of an edited window edge: the picked day and time as entered. */
const stationWindowKey = (instant: Dayjs, timeZone: string = STATION_TIME_ZONE): string =>
    formatAtStation(instant.toDate(), 'yyyy-MM-dd HH:mm', 'en', timeZone);

const EditVenueBookingPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        options: countryOptions,
        toLabel: toCountryLabel,
        toValue: toCountryValue,
    } = useCountryOptions();
    const navigate = useNavigate();
    const [cities, setCities] = useState<string[]>([]);
    const [apartmentOptions, setApartmentOptions] = useState<{id: string, name: string}[]>([]);
    const [loadingCities, setLoadingCities] = useState(true);
    const [performRevert, setPerformRevert] = useState(false);
    const [open, setOpen] = useState(false);
    const [alreadyPaid, setAlreadyPaid] = useState<number>(0);
    const [discount, setDiscount] = useState<number>(0);
    // #1000 Step 7C: reason for a manual price adjustment (sent with the
    // override when the admin changes the price).
    const [priceAdjustmentReason, setPriceAdjustmentReason] = useState<string>('');
    const [, setLoadingApartments] = useState(false);
    const [originalApartment, setOriginalApartment] = useState<string>();
    const [bookingFor, setBookingFor] = useState<string>('customer');
    const [editDataLoaded, setEditDataLoaded] = useState<boolean>(true);
    const [loadingEditBookingOnlySave, setLoadingEditBookingOnlySave] = useState(false);
    const [loadingEditBookingSaveAndSend, setLoadingEditBookingSaveAndSend] = useState(false);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });
    const startTime = useSelector((state: RootState) => state.bookingCreation.editBookingDetails.startTime);
    const endTime = useSelector((state: RootState) => state.bookingCreation.editBookingDetails.endTime);
    // The booking's window as loaded — see previewArgs (#1713).
    const originalWindow = useRef<{ start: string, end: string } | null>(null);

    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';

    const { data, isFetching } = useGetBookingDetailsQuery({
        orderId,
    });
    // M8 approval (#1809): on a reservation still awaiting approval, "save and
    // send email" IS the approval (the server records it once the mail is
    // attempted), so the button and its confirm say so. "Save only" leaves it
    // awaiting.
    const sendCopy = saveAndSendCopyKeys(toApprovalState(data?.approval?.state) === 'awaiting');
    // The screen serves the event flow: venue, and hall, which shares it. The
    // booking's own use case decides; venue while it loads, as before.
    const useCase = data?.useCase ?? 'venue';
    const noun = roomNounOf(useCase);

    // #1810: organisation (unit) and the invoice recipient's cost centre.
    // Seeded ONCE per booking from the API — afterwards the form owns it, so a
    // background refetch cannot overwrite what the operator is editing.
    const [attribution, setAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    const attributionSeededFor = useRef<string | null>(null);
    useEffect(() => {
        // Only a stored attribution that was actually READ seeds the form: on
        // a failed read the fields stay empty and nothing is sent, and a later
        // successful refetch seeds them then — an empty seed from the failed
        // read must never be what a save sends back.
        if (!data || data.id !== orderId || data.attributionStatus !== 'ok') return;
        if (attributionSeededFor.current === orderId) return;
        attributionSeededFor.current = orderId;
        setAttribution({
            organizationId: data.organizationId ?? null,
            organizationUnitId: data.organizationUnitId ?? null,
            costCenter: data.costCenter ?? '',
        });
    }, [data, orderId]);

    // #1808: the booking's class, changeable here as on create (staging
    // finding 6). A change reprices the stay under the new class on save, and
    // the price preview quotes that already.
    const {
        customerClassId,
        setCustomerClassId,
        seeded: classSeeded,
        classFields,
        // "No override" is named after the class clearing would apply.
        noOverrideClassId,
    } = useEditBookingCustomerClass(data, orderId);

    const editRoomRentalBooking = useEditRoomRentalBooking(orderId);
    const fetchApartmentDetails = useFetchApartmentDetails(true);

    const breadcrumbItems = [
        { label: t('booking_details.booking'), href: paths.rentalBooking },
        { label: data?.bookingNumber ?? '', href: makeRentalBookingDetailsUrl(orderId) },
        { label: t('booking_details.edit'), isActive: true },
    ];
    const isInvoiceIssuingTenant = useIsInvoiceIssuingTenant();
    const invoiceNumber = useSelector(
        (state: RootState) => state.bookingCreation.editBookingDetails?.invoiceNumber,
    );
    const onFirstNameChange = (firstName: string) => dispatch(setEditFirstName({ firstName }));
    const onLastNameChange = (lastName: string) => dispatch(setEditLastName({ lastName }));
    const onPhoneChange = (phone: string) => {
        dispatch(setEditPhone({ phone: phone }));
    };
    const onCompanyNameChange = (company: string) => {
        dispatch(setEditCompany({ company: company }));
    };
    const onCommentChange = (comment: string) => {
        dispatch(setEditComment({ comment: comment }));
    };
    const onEmailNoteChange = (emailNote: string) => {
        dispatch(setEditEmailNote({ emailNote: emailNote }));
    };
    const onEmailChange = (email: string) => dispatch(setEditEmail({ email }));

    const onAddressChange = (address: string) => dispatch(setEditAddress({ address }));
    const onCityChange = (city: string) => dispatch(setEditPersoCity({ city }));
    const onCountryChange = (country: string) => {
        dispatch(setEditCountry({ country: country }));
    };
    const onZipCodeChange = (zipCode: string) => {
        dispatch(setEditZipCode({ zipCode }));
    };

    const onSomeoneElseFirstNameChange = (firstName: string) => {
        dispatch(setSomeoneElseEditFirstName({ firstName: firstName }));
    };
    const onSomeoneElseLastNameChange = (lastName: string) => {
        dispatch(setSomeoneElseEditLastName({ lastName: lastName }));
    };
    const onSomeoneElseEmailChange = (email: string) => {
        dispatch(setSomeoneElseEditEmail({ email: email }));
    };
    const onSomeoneElsePhoneChange = (phone: string) => {
        dispatch(setSomeoneElseEditPhone({ phone: phone }));
    };

    const selectedApartment = useSelector((state: RootState) => state.bookingCreation.selectedEditApartment);
    // #3219: the stored booking is read on its own location's clock; the
    // picked days and times are built on the clock of the venue being booked
    // (the same location unless the operator moves the booking).
    const bookingTimeZone = zoneOfLocation(data?.locationTimeZone);
    const timeZone = useLocationTimeZone(selectedApartment?.locationId, data?.locationTimeZone);

    const personalDetails = useSelector((state: RootState) => state.bookingCreation.personalData);

    const someoneElseDetails = useSelector((state: RootState) => state.bookingCreation.someoneElseData);

    const city = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.city);
    const startDate = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.checkIn);
    const endDate = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.checkOut);
    const numberOfGuests = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.numberOfGuests);
    const comment = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.comment);
    const createdBy = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.createdBy);
    const emailNote = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.emailNote);
    // Extract start and end times directly from the dates

    const eventType = useSelector((state: RootState) => state.bookingCreation.editBookingDetails?.eventType);
    // Configured-else-standard venue vocabulary. The stored value is passed in
    // so a booking carrying a type the tenant has since removed still renders
    // instead of showing an empty select (see useEventTypeOptions).
    const eventTypeOptions = useEventTypeOptions(eventType, data?.useCase);

    const apartments = useSelector((state: RootState) => state.bookingCreation.apartments);

    const personalDataValidation: PersonalDataValidation = useSelector((state: RootState) => state.bookingCreation.personalDataValidation);

    const editBookingDetails = useSelector((state: RootState) => state.bookingCreation.editBookingDetails);

    const additionalServices = useSelector((state: RootState) => state.bookingCreation.editBookingDetails.additionalService ?? []);

    const selectedApartmentDetails = useSelector((state: RootState) => state.bookingCreation.selectedEditApartmentDetails);

    const confirmEnabled = useSelector((state: RootState) => state.bookingCreation.confirmEnabled);

    const allAdditionalServices: AdditionalServiceType[] | null =
            selectedApartmentDetails?.assetAdditionalServices ?? null;

    // #1734: only restrict the service menu to what existed at booking time
    // while the booking is still on its original asset. Switching asset during
    // edit re-prices against that asset's current offering, so its newer
    // services must stay selectable.
    const bookedAssetStillSelected =
        !!data?.apartment?.id && selectedApartment?.assetId === data.apartment.id;

    const { servicesUnavailableAtVenue, markPendingRemap } = useEditServiceRemapOnAssetSwitch();

    const styles = {
        paper: (theme: any) => ({
            padding: '20px',
        }),
    };

    const generateAvailableDates = (minDate?: Dayjs | null) => {
        const today = new Date();
        const start = minDate || dayjs(today).startOf('day');
        const end = dayjs().add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const generateAvailableCheckOutDates = (checkInDate: Dayjs | null) => {
        if (!checkInDate) return generateAvailableDates();

        // Allow same day for venue bookings
        const start = checkInDate;
        const end = dayjs()?.add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date?.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const availableCheckInDates = generateAvailableDates();
    const availableCheckOutDates = generateAvailableCheckOutDates(startDate);

    const handleDateChange = (start: Dayjs | null, end: Dayjs | null) => {
        if (start) {
            // Clear the *edit* checkout, not the create-flow one — otherwise a
            // restarted selection (outside click) leaves the stale checkout in
            // place and the form shows an inverted range like "04 Aug – 03 Aug".
            // Cleared first so setEditCheckInDate validates against it.
            dispatch(clearEditCheckOutDate());
            dispatch(setEditCheckInDate({ checkInDate: start.startOf('day') }));
        }
        if (end) {
            dispatch(setEditCheckOutDate({ checkOutDate: end }));
        }
    };

    const handleStartTimeChange = (value: string) => {
        dispatch(setStartTime({ startTime: value }));
    };

    const handleEndTimeChange = (value: string) => {
        dispatch(setEndTime({ endTime: value }));
    };

    // The booking's own event-flow use case (venue or hall), venue until it loads.
    const { data: locations } = useGetLocationListQuery(useCase);

    let availableStartTimes: string[] = [];
    let availableEndTimes: string[] = [];
    if (startDate && endDate) {
        const { availableStartTimes: startTimes, availableEndTimes: endTimes } = getAvailableBookingTimes(startTime, startDate, endDate, timeZone);
        availableStartTimes = startTimes;
        availableEndTimes = endTimes;
    }

    const currentLanguage = i18n.language;

    useEffect(() => {
        if (apartments) {
            setApartmentOptions(sortByLocaleName(apartments.map((apart) => {
                const displayName = apart?.langNames?.[currentLanguage]?.title || apart?.name;
                return {
                    id: apart.id,
                    name: displayName,
                };
            }), (option) => option.name));
        }
    }, [apartments, currentLanguage]);

    useEffect(() => {
        // The booking's times as the edit state holds them (station clock).
        const bookingStartTime = startDate ? startTime || null : null;
        const bookingEndTime = endDate ? endTime || null : null;
        dispatch(clearBookingCreationStates());
        if (bookingStartTime) {
            dispatch(setStartTime({ startTime: bookingStartTime }));
        }
        if (bookingEndTime) {
            dispatch(setEndTime({ endTime: bookingEndTime }));
        }
    }, []);

    useEffect(() => {
        if (locations && locations.length > 0) {
            setCities(Array.from(new Set(locations.map((loc) => loc.address.city))));
            setLoadingCities(false);
        }
    }, [locations]);

    const getDateTime = (date: Dayjs | null, time: string | null | undefined) => {
        if (!date || !time) return null;
        return stationDateTime(date, time, timeZone);
    };

    const combinedStartDateTime = getDateTime(startDate, startTime ?? null);
    const combinedEndDateTime = getDateTime(endDate, endTime ?? null);

    const fetchApartments = useFetchApartments(
        combinedStartDateTime,
        combinedEndDateTime,
        city,
        numberOfGuests,
        orderId,
        data?.useCase, // useCase from booking data
    );
    useEffect(() => {
        const getApartments = async () => {
            if (city && combinedStartDateTime && combinedEndDateTime && numberOfGuests && fetchApartments) {
                setLoadingApartments(true);
                await fetchApartments({
                    includeCurrentApartment: true,
                });
                setLoadingApartments(false);
            }
        };
        getApartments();
    }, [city, combinedStartDateTime, combinedEndDateTime, numberOfGuests, fetchApartments]);

    useEffect(() => {
        if (data && performRevert && apartments && apartments.length > 0) {
            const selected = apartments?.find((apartment) => apartment.assetId === data.apartment.id);
            if (selected) {
                dispatch(setSelectedEditApartment(selected));
            }
            setPerformRevert(false);
        }
    }, [apartments, performRevert]);

    useEffect(() => {
        setEditDataLoaded(true);
    }, [orderId]);

    useEffect(() => {
        if (data && editDataLoaded) {
            // The booking's window as loaded, composed exactly as previewArgs
            // composes it (date + HH:mm, the same fallback the reducer uses
            // for absent times) — so an untouched booking compares equal.
            originalWindow.current = {
                start: windowKey(data.checkIn, data.startTime, zoneOfLocation(data.locationTimeZone)),
                end: windowKey(data.checkOut, data.endTime, zoneOfLocation(data.locationTimeZone)),
            };
            dispatch(setEditBookingDetails(data));
            setEditDataLoaded(false);
            if (Object.keys(data.guestDetails).length > 0) {
                setBookingFor('someone_else');
            } else {
                setBookingFor('customer');
            }
        }
    }, [data]);

    useEffect(() => {
        if (data && apartments && apartments.length > 0 && !originalApartment) {
            const selected = apartments.find((apartment) => apartment.assetId === data.apartment.id);
            if (selected) {
                dispatch(setSelectedEditApartment(selected));
                setOriginalApartment(selected.name);
            }
        }
    }, [data, apartments]);

    // Clear apartment details left over from a previously edited booking so we never
    // briefly show another asset's add-on services before this booking's details load.
    useEffect(() => {
        dispatch(setSelectedEditApartmentDetails(null));
    }, [dispatch]);

    useEffect(() => {
        const getApartmentDetails = async () => {
            dispatch(setSelectedEditApartmentDetails(null));
            if (selectedApartment) {
                await fetchApartmentDetails();
            }
        };
        getApartmentDetails();
        if (selectedApartment) {
            setLoadingApartments(false);
        }
    }, [selectedApartment]);

    useEffect(() => {
        const getApartments = async () => {
            if (city && startDate && endDate && numberOfGuests && fetchApartments) {
                setLoadingApartments(true);
                await fetchApartments({
                    includeCurrentApartment: true,
                });
                setLoadingApartments(false);
            }
        };
        getApartments();
    }, [city, startDate, endDate, numberOfGuests]);


    const compareApartments = useCallback((apartmentOne?: string, apartmentTwo?: string) => {
        return apartmentOne?.replace(/["']/g, '') === apartmentTwo?.replace(/["']/g, '');
    }, []);

    // Booking price single source of truth (#1203): the price box + save payload
    // read the backend breakdown from POST /admin/order/price-preview.
    const previewArgs: PricePreviewArgs | undefined = useMemo(() => {
        const assetId = selectedApartment?.assetId;
        if (!assetId || !startDate || !endDate) return undefined;
        // #1713: a CHANGED window is quoted with its times, as the save
        // reprices it (hourly / half-day units and per-hour services depend
        // on them). The UNCHANGED window keeps the calendar-date request: the
        // backend matches it against the stored booking by local day and
        // bills the booked price (#1740) — sending instants there would turn
        // that match into an exact-timestamp comparison and risk re-quoting
        // an untouched booking.
        const timed = startTime && endTime
            ? {
                start: stationDateTime(startDate, startTime, timeZone),
                end: stationDateTime(endDate, endTime, timeZone),
            }
            : null;
        const windowChanged = !!timed && (
            originalWindow.current === null
            || stationWindowKey(timed.start, timeZone) !== originalWindow.current.start
            || stationWindowKey(timed.end, timeZone) !== originalWindow.current.end
        );
        return {
            useCase,
            assetId,
            checkIn: windowChanged && timed ? timed.start.toISOString() : dayjs(startDate).format('YYYY-MM-DD'),
            checkOut: windowChanged && timed ? timed.end.toISOString() : dayjs(endDate).format('YYYY-MM-DD'),
            services: additionalServices.map((s) => ({
                serviceId: s.id,
                quantity: s.quantity ?? 1,
            })),
            // #1734: price already-booked services from their snapshot rather
            // than today's catalog rate, so editing never re-prices the booking.
            ...(data?.id ? { orderId: data.id } : {}),
            // The class picked on this form: the backend re-quotes the stay
            // when it differs from the stored one, as the save reprices it.
            ...classFields,
            discount,
            amountPaid: alreadyPaid ?? 0,
        };
    }, [
        selectedApartment?.assetId, startDate, endDate, startTime, endTime, additionalServices,
        discount, alreadyPaid, useCase, data?.id, timeZone, classFields,
    ]);

    const { breakdown, previewStatus } = usePricePreview(previewArgs);
    // PO, 2026-10-30: the backend says whether this booking's customer mail
    // may go out; "save" stays available either way.
    const mailRefusal = customerMailRefusalOf(breakdown?.customerMail);
    const guardPriceOverride = usePriceOverrideGuard(setSnackbarData);

    const apartmentOptionNames = apartmentOptions.map(option => option.name);
    const cleanApartmentNames = apartmentOptionNames.map(
        (apartment) => apartment.replace(/["']/g, ''),
    );
    const apartmentHasError = originalApartment !== undefined &&
        !cleanApartmentNames.includes(originalApartment.replace(/["']/g, '')) &&
        compareApartments(originalApartment, selectedApartment?.name);

    // Guests may not exceed the selected object's capacity. Only a count
    // changed in this session blocks the save — see useGuestCapacityGuard.
    const {
        capacity: objectCapacity,
        exceeds: guestsExceedCapacity,
        blocksSave: guestsBlockSave,
    } = useGuestCapacityGuard({
        selectedObject: selectedApartment,
        selectedObjectDetails: selectedApartmentDetails,
        guests: numberOfGuests,
        savedGuests: data?.numberOfGuests,
    });

    const saveButtonDisabled = loadingEditBookingSaveAndSend
        || loadingEditBookingOnlySave
        || !confirmEnabled
        || apartmentHasError
        || guestsBlockSave;
    
    const saveOnlyCheck = isEditBookingValid(
        {
            orderData: {
                city: editBookingDetails.city,
                checkInDate: editBookingDetails.checkIn,
                checkOutDate: editBookingDetails.checkOut,
                startTime: editBookingDetails.startTime ?? '',
                endTime: editBookingDetails.endTime ?? '',
                eventType: editBookingDetails.eventType,
                numberOfGuests: editBookingDetails.numberOfGuests,
                amount: 1,
                comment: editBookingDetails.comment,
            },
            apartment: selectedApartment,
            personalData: personalDetails,
            someoneElseData: someoneElseDetails,
        },
        ['firstName', 'lastName'],
    );


    const saveOnlyButtonDisabled = loadingEditBookingSaveAndSend
        || loadingEditBookingOnlySave
        || apartmentHasError
        || guestsBlockSave
        || !saveOnlyCheck;

    const handleEditBooking = (saveMode: 'save_only' | 'save_and_send_email') => {
        const editBooking = async (saveMode: 'save_only' | 'save_and_send_email') => {
            if (saveMode === 'save_only') {
                setLoadingEditBookingOnlySave(true);
            } else {
                setLoadingEditBookingSaveAndSend(true);
            }

            // Combine date and time for check-in and check-out
            // The picked day at the picked time on the station's clock; a day
            // without a time is station midnight, not the browser's.
            const checkInWithTime = pickedInstantOrNow(startDate, startTime, timeZone);
            const checkOutWithTime = pickedInstantOrNow(endDate, endTime, timeZone);

            const editRoomRentalBookingPayload: EditRoomRentalBookingPayload = {
                checkIn: checkInWithTime,
                checkOut: checkOutWithTime,
                city: city ?? '',
                invoiceNumber: invoiceNumber ?? '',
                numberOfGuests: numberOfGuests,
                apartmentId: selectedApartment?.assetId ?? '',
                // #2650: name the offer, not just the object. A divided room's
                // objects all belong to a segment, so without this the whole
                // room silently books one of its parts.
                groupId: selectedApartment?.groupId ?? '',
                locationId: selectedApartment?.locationId ?? '',
                additionalServices: additionalServices.map((additionalService) => ({
                    serviceId: additionalService.id,
                    name: additionalService.serviceName,
                    quantity: additionalService.quantity ?? 1,
                })),
                customerDetails: {
                    firstName: personalDetails.firstName,
                    lastName: personalDetails.lastName,
                    email: personalDetails.email,
                    phone: personalDetails.phone,
                    address: personalDetails.address,
                    country: personalDetails.country,
                    city: personalDetails.city,
                    zipCode: personalDetails.zipCode,
                    company: personalDetails.company,
                },
                ...attributionPayloadForEdit(attributionSeededFor.current === orderId ? 'ok' : undefined, attribution),
                // #1808: the picked class, once seeded; a change reprices on save.
                ...classFields,
                someoneElseDetails:
                    bookingFor === 'customer'
                        ? {}
                        : {
                            firstName: someoneElseDetails.firstName,
                            lastName: someoneElseDetails.lastName,
                            email: someoneElseDetails.email,
                            phone: someoneElseDetails.phone,
                        },
                useCase,
                // Every money-bearing field below is omitted when the preview has
                // not resolved. `adminEditBookingRoomRental` spreads each one
                // conditionally, so an absent field leaves the stored value
                // untouched — the only honest answer when we do not yet know the
                // price. The previous `?? 'unpaid'` / `?? 0` defaults invented one
                // instead: they downgraded an already-paid booking to unpaid, and
                // re-billed it to 0,00 € via the manual override. Dates, contact
                // details and comment still save, so this can never block an edit.
                ...(breakdown
                    ? {
                        // BE-derived (#1203).
                        paymentStatus: breakdown.paymentStatus,
                        // #1000 Step 7C: the price adjustment rides the manual
                        // final-price override (SSoT re-bill), never the legacy
                        // discount voucher — so `discount` is always sent as 0 (a
                        // surcharge would otherwise be a negative discount, which
                        // the API rejects). The signed adjustment lives in
                        // manualFinalPrice = BE total.
                        ...(discount !== 0
                            ? {
                                // Guarded above, not clamped: a negative total
                                // means the discount is stale, and clamping it
                                // to 0 re-bills the booking as free.
                                manualFinalPrice: Number(breakdown.total.toFixed(2)),
                                priceAdjustmentReason: priceAdjustmentReason,
                            }
                            // No adjustment: clear a previously-stored override so
                            // setting it back to 0 takes effect; else leave
                            // un-overridden.
                            : data?.payment?.manualFinalPrice != null
                                ? { clearPriceOverride: true }
                                : {}),
                    }
                    : {}),
                discount: 0,
                alreadyPaid: alreadyPaid ?? 0,
                comment: comment,
                emailNote: emailNote,
                eventType: eventType,
            };
            const { success, reason, mailBlocked } = await editRoomRentalBooking(editRoomRentalBookingPayload, saveMode);
            if (success) {
                setSnackbarData({
                    open: true,
                    // Saved — but a refused mail is said, never shown as a plain success.
                    ...savedOutcomeToast(t, mailBlocked, t('edit_booking.response.success')),
                });
                setTimeout(() => {
                    navigate(makeRentalBookingDetailsUrl(orderId));
                }, 1500);
                dispatch(clearBookingCreationStates());
                if (saveMode === 'save_only') {
                    setLoadingEditBookingOnlySave(false);
                } else {
                    setLoadingEditBookingSaveAndSend(false);
                }
            } else {
                setSnackbarData({
                    open: true,
                    message: t(editFailureMessageKey(reason)),
                    severity: 'error',
                });
                if (saveMode === 'save_only') {
                    setLoadingEditBookingOnlySave(false);
                } else {
                    setLoadingEditBookingSaveAndSend(false);
                    setOpen(false);
                }
            }
        };
        // Guarded out here rather than inside `editBooking`: the save flow is
        // already at the cognitive-complexity limit, and refusing before it
        // starts is where the check belongs anyway.
        if (!guardPriceOverride(breakdown)) return;
        editBooking(saveMode);
    };

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (!loadingCities && cities?.length === 1 && !city) {
            dispatch(setCity({ city: cities[0] }));
        }
    }, [cities, city, loadingCities, dispatch]);

    const handleRevertChanges = () => {
        if (data) {
            dispatch(setRevertEditBookingReservation(data));
            setPerformRevert(true);
        }
    };

    useEffect(() => {
        setAlreadyPaid(editBookingDetails.payment.amountPaid ?? 0);
        setDiscount(editBookingDetails.payment.couponApplied ?? 0);
    }, [editBookingDetails.payment.amountPaid, editBookingDetails.payment.couponApplied]);

    const apartmentNotFound = useMemo(() => {
        if (!originalApartment) return false;
        const isApartmentSelected = compareApartments(selectedApartment?.name, originalApartment);
        const cleanedOriginalApartment = originalApartment.replace(/["']/g, '');
        return isApartmentSelected && !cleanApartmentNames.includes(cleanedOriginalApartment);
    }, [cleanApartmentNames, selectedApartment, originalApartment]);

    const isCanceled = editBookingDetails.status === 'canceled';

    return (
        <Container maxWidth={false} className={'px-0 sm:px-4'}>
            {isFetching ? (
                <Box sx={{ py: 3 }}>
                    <div className={'flex w-full h-[80vh] items-center justify-center'}>
                        <CircularProgress />
                    </div>
                </Box>
            ) : (
                <Box sx={{ py: 3, pt: 3 }}>
                    <PageHeader>
                        <PageHeadline>
                            {t('booking_details.title')}
                        </PageHeadline>
                    </PageHeader>                    
                    <div className={'mb-6'}>
                        <CustomSeparator items={breadcrumbItems} />
                    </div>
                    <Paper sx={styles.paper}>
                        {/* Header summary */}
                        <div
                            className={`
                        flex items-start justify-between mb-4 bg-[#F5F5F5]
                        p-3
                        `}
                        >
                            <div className={'flex flex-1 flex-col'}>
                                <div className={'flex items-center mb-2'}>
                                    <div className={'flex flex-col lg:flex-row'}>
                                        <h1 className={'text-2xl font-medium mr-4'}>{data?.bookingNumber}</h1>
                                        <div className={'flex flex-row mt-2 xl:mt-0'}>
                                            <StatusBadge
                                                type={editBookingDetails?.status ?? 'booked'}
                                                isEditing={true}
                                                options={[
                                                    {
                                                        label: t('booking_details.status.active'),
                                                        value: 'active',
                                                    },
                                                    {
                                                        label: t('booking_details.status.booked'),
                                                        value: 'booked',
                                                    },
                                                    {
                                                        label: t('booking_details.status.canceled'),
                                                        value: 'canceled',
                                                    },
                                                    {
                                                        label: t('booking_details.status.finished'),
                                                        value: 'finished',
                                                    },
                                                ]}
                                            />
                                            <div className={'mx-2'}></div>
                                            <StatusBadge
                                                // Covered reads "Abgedeckt", as on the details page.
                                                type={editPaymentBadgeStatus(data, editBookingDetails?.payment_status)}
                                                showDropdown={false}
                                                isEditing={true}
                                                options={[
                                                    {
                                                        label: t('booking_details.status.paid'),
                                                        value: 'paid',
                                                    },
                                                    {
                                                        label: t('booking_details.status.unpaid'),
                                                        value: 'unpaid',
                                                    },
                                                ]}
                                                onSelect={(val) => Logger(val)}
                                            />
                                            <div className={'mx-2'}></div>
                                            <StatusBadge
                                                type={editBookingDetails?.isMailSent === true ? 'sent' : 'not_sent'}
                                                showDropdown={false}
                                                isEditing={false}
                                                options={[
                                                    {
                                                        label: t('booking_details.mail_sent.mail_sent'),
                                                        value: 'sent',
                                                    },
                                                    {
                                                        label: t('booking_details.mail_sent.mail_not_sent'),
                                                        value: 'not_sent',
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={'flex w-full gap-4 flex-col items-start sm:flex-row sm:justify-center sm:items-center'}>
                                    <div className={'flex-1'}>
                                        <p className={'text-sm text-gray-500'}>
                                            {t('booking_details.created_on')}:{' '}
                                            <span className={'font-bold'}>{formatDateForDisplay(data?.created_at!, bookingTimeZone)}</span>
                                        </p>
                                        <p className={'text-sm text-gray-500'}>
                                            {t('booking_details.last_update')}:{' '}
                                            <span className={'font-bold'}>{formatDateForDisplay(data?.updated_at!, bookingTimeZone)}</span>
                                        </p>
                                    </div>
                                    {/* #1637: retired for Mode A, with or without a number circle (no
                                        scheme, no invoicing: ADR-0012, PO 2026-09-29). The statutory number is allocated by
                                        kolula and lives on the invoice record; a free-text field beside it
                                        would let an operator type a competing one. Mode C tenants keep it —
                                        their ERP's number belongs here. */}
                                    {!isInvoiceIssuingTenant && (
                                        <div className={'flex-1 w-full'}>
                                            <Typography variant={'body2'} className={'mb-2 font-bold text-foreground'}>
                                                {t('create_booking.reservation_details.invoice_nr')}
                                            </Typography>
                                            <InputField
                                                onChange={(value) =>
                                                    dispatch(
                                                        setEditInvoiceNumber({
                                                            invoiceNumber: value,
                                                        }),
                                                    )
                                                }
                                                disabled={isCanceled}
                                                value={invoiceNumber}
                                                label={
                                                    t(
                                                        'create_booking.reservation_details.invoice_nr',
                                                    ) + ' *'
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Typography variant={'h3'} className={'mb-3 mt-5'}>
                            {t('create_booking.reservation_details.title')}
                        </Typography>
                        <Grid
                            container
                            sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                display: 'flex',
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                                className={'w-[100%] xl:w-[30%] mt-1 xl:mt-0 mb-8 xl:mb-0 flex justify-end items-end order-1 xl:order-2'}
                            >
                                <VenueReservationSummary
                                    bookingDetails={editBookingDetails}
                                    isCreateBooking={false}
                                    isEditBooking={true}
                                    breakdown={breakdown}
                                    previewStatus={previewStatus}
                                    couponApplied={discount}
                                    amountPaid={alreadyPaid}
                                    onAlreadyPaidChange={(newAlreadyPaid) => setAlreadyPaid(newAlreadyPaid)}
                                    onDiscountChange={(newDiscount) => setDiscount(newDiscount)}
                                    numberOfGuests={numberOfGuests}
                                    startDate={startDate?.toISOString()}
                                    endDate={endDate?.toISOString()}
                                    coveredBy={coveredByOf(data)}
                                />
                                <PriceAdjustmentReasonField
                                    discount={discount}
                                    value={priceAdjustmentReason}
                                    onChange={setPriceAdjustmentReason}
                                    disabled={isCanceled}
                                />
                            </Grid>
                            <Grid container spacing={2} className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}>
                                <Grid item xs={12} sm={6}>
                                    <DateRangeField
                                        from={startDate ? dayjs(startDate) : null}
                                        to={endDate ? dayjs(endDate) : null}
                                        onChange={(from, to) => handleDateChange(from, to)}
                                        disabled={isCanceled}
                                        disablePast
                                        allowedStartDays={availableCheckInDates}
                                        allowedEndDays={availableCheckOutDates}
                                        maxDate={dayjs().add(18, 'month').endOf('month')}
                                        label={t('create_booking.reservation_details.booking_period') + ' *'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {
                                        isCanceled ?
                                            <TimePickerComponent
                                                disabled={true}
                                                value={startTime ? dayjs(startTime, 'HH:mm') : null}
                                                onChange={(time) => dispatch(setStartTime({ startTime: time?.format('HH:mm') || '' }))}
                                                label={t('create_booking.reservation_details.start_time')}
                                            />
                                            :
                                            <SelectField
                                                options={availableStartTimes}
                                                value={startTime ?? ''}
                                                onChange={handleStartTimeChange}
                                                label={t('create_booking.reservation_details.start_time')}
                                                disabled={false}
                                            />
                                    }
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {
                                        isCanceled ?
                                            <TimePickerComponent
                                                disabled={true}
                                                value={endTime ? dayjs(endTime, 'HH:mm') : null}
                                                onChange={(time) => dispatch(setEndTime({ endTime: time?.format('HH:mm') || '' }))}
                                                label={t('create_booking.reservation_details.end_time')}
                                            />
                                            :
                                            <SelectField
                                                options={availableEndTimes}
                                                value={endTime ?? ''}
                                                onChange={handleEndTimeChange}
                                                label={t('create_booking.reservation_details.end_time')}
                                                disabled={isCanceled}
                                            />

                                    }
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <SelectField
                                        options={cities ?? ['']}
                                        value={city ?? ''}
                                        onChange={(value) => dispatch(setCity({ city: value }))}
                                        disabled={cities.length === 1 || isCanceled}
                                        loading={loadingCities}
                                        label={t('create_booking.reservation_details.city') + ' *'}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        type={'number'}
                                        min={1}
                                        integerOnly
                                        onChange={(value) =>
                                            dispatch(
                                                setEditNumberOfGuests({
                                                    numberOfGuests: Number(value),
                                                }),
                                            )
                                        }
                                        disabled={isCanceled}
                                        value={String(numberOfGuests ?? '')}
                                        error={guestsExceedCapacity}
                                        errorText={t(
                                            'create_booking.reservation_details.guests_exceed_object_capacity',
                                            { capacity: objectCapacity ?? 0 },
                                        )}
                                        label={t('create_booking.reservation_details.number_of_guests') + ' *'}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <SelectField
                                        // Without this the edit screen falls back
                                        // to the select's default "No options
                                        // available", which cannot say why. The
                                        // create screen already names the reason.
                                        noOptions={t(
                                            `create_booking.reservation_details.no_${noun}s_match`,
                                        )}
                                        options={apartmentOptions.map(option => option.name) ?? []}
                                        value={selectedApartment?.name ?? ''}
                                        onChange={(selectedName) => {
                                            if (apartments) {
                                                const selectedOption = apartmentOptions.find(option => option.name === selectedName);
                                                if (selectedOption) {
                                                    const selected = apartments.find(
                                                        (apartment) => apartment.id === selectedOption.id,
                                                    );
                                                    if (selected) {
                                                        dispatch(setSelectedEditApartment(selected));
                                                        markPendingRemap();
                                                    }
                                                }
                                            }
                                        }}
                                        disabled={isCanceled}
                                        error={apartmentHasError}
                                        label={t(`create_booking.reservation_details.${noun}`) + ' *'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <EventTypeField
                                        groupId={selectedApartment?.groupId}
                                        options={eventTypeOptions}
                                        value={eventType}
                                        onChange={(eventType) => dispatch(setEventType({mode: 'edit', eventType}))}
                                        disabled={isCanceled}
                                    />
                                </Grid>
                                {/* #1808: the same select as create, pre-filled with the
                                    stored override; absent unless the tenant has more than
                                    the seeded default class, or before the booking's class
                                    was read. */}
                                {classSeeded && (
                                    <Grid item xs={12} sm={6}>
                                        <BookingCustomerClassSelect
                                            value={customerClassId}
                                            inheritedClassId={noOverrideClassId}
                                            onChange={setCustomerClassId}
                                            disabled={isCanceled}
                                        />
                                    </Grid>
                                )}
                                {selectedApartment && (
                                    <Grid item xs={12}>
                                        <Typography variant={'body1'} fontWeight={'bold'} sx={{ mt: 2, color: 'gray' }}>
                                            {/* eslint-disable-next-line max-len */}
                                            {`${extractAddress(selectedApartment?.name)}, ${selectedApartment?.street}, ${selectedApartment?.postalCode} ${
                                                selectedApartment?.city
                                            }`}
                                        </Typography>
                                        {apartmentNotFound && (
                                            <Alert
                                                severity={'warning'}
                                                sx={{
                                                    mt: 2,
                                                    backgroundColor: '#FCEEC680',
                                                    color: '#333333',
                                                    fontSize: '14px',
                                                    '& .MuiAlert-icon': {
                                                        color: '#F3BB1B',
                                                    },
                                                    '& a': {
                                                        color: '#333333',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                    },
                                                }}
                                            >
                                                <Trans
                                                    i18nKey={`create_booking.reservation_details.original_${noun}_not_available`}
                                                    values={{ venue: originalApartment }}
                                                    components={[
                                                        <button
                                                            type={'button'}
                                                            onClick={handleRevertChanges}
                                                            style={{
                                                                all: 'unset',
                                                                color: 'inherit',
                                                                cursor: 'pointer',
                                                                textDecoration: 'underline',
                                                            }}
                                                        />,
                                                    ]}
                                                />
                                            </Alert>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                        <div className={'w-[100%] xl:w-[70%]'}>
                            <Typography variant={'h3'} className={'mb-2'}>
                                {t('create_booking.additional_services.title')}
                            </Typography>
                            {selectedApartment ? (
                                selectedApartmentDetails ? (
                                    isCanceled ?
                                        <BookingDetailsAdditionalServices
                                            additionalServices={additionalServices}
                                            allAdditionalServices={
                                                allAdditionalServices ?? editBookingDetails?.allAdditionalServices!
                                            }
                                        />
                                        :
                                        <BookingEditDetailsAdditionalServices
                                            additionalServices={additionalServices}
                                            allAdditionalServices={
                                                allAdditionalServices ?? editBookingDetails?.allAdditionalServices!
                                            }
                                            bookingCreatedAt={bookedAssetStillSelected ? data?.created_at : undefined}
                                            bookedCompulsoryServices={data?.payment?.compulsory}
                                            unavailableServices={servicesUnavailableAtVenue}
                                        />
                                ) : (
                                    <div
                                        className={
                                            'flex items-center space-x-2 text-gray-500'
                                        }
                                    >
                                        <CircularProgress
                                            className={'text-gray-500'}
                                            size={20}
                                        />
                                        <Typography className={'text-gray-500'}>
                                            {t('common.loading')}
                                        </Typography>
                                    </div>
                                )
                            ) : (
                                <Typography>
                                    {t(`create_booking.additional_services.note_${noun}`)}
                                </Typography>
                            )}
                        </div>
                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                        <Typography variant={'h3'} className={'mb-2'}>
                            {t('create_booking.customer_details.title')}
                        </Typography>

                        <Grid container={true} spacing={2} className={'w-[100%] xl:w-[70%]'} sx={{ justifyContent: 'flex-start' }}>
                            {/* First row with two Selects */}
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onFirstNameChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.firstName ?? ''}
                                    label={t('create_booking.customer_details.first_name') + ' *'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onLastNameChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.lastName ?? ''}
                                    label={t('create_booking.customer_details.last_name') + ' *'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onEmailChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.email ?? ''}
                                    label={t('create_booking.customer_details.email')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <PhoneNumberComponent
                                    enabled={!isCanceled}
                                    initialValue={personalDetails?.phone ?? ''}
                                    onChange={onPhoneChange}
                                    valid={personalDataValidation.phone}
                                    required={false}
                                    label={t('create_booking.customer_details.phone')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onCompanyNameChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.company ?? ''}
                                    label={t('create_booking.customer_details.company')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <BookingAttributionFields
                                    value={attribution}
                                    onChange={setAttribution}
                                    onOrganizationPicked={(prefill) => applyContactPrefill(prefill, {
                                        company: onCompanyNameChange,
                                        address: onAddressChange,
                                        zipCode: onZipCodeChange,
                                        city: onCityChange,
                                        country: onCountryChange,
                                    })}
                                    disabled={isCanceled}
                                    organizationLocked={data?.coveredByOrg === true}
                                    loadFailed={data?.attributionStatus === 'failed'}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onAddressChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.address ?? ''}
                                    label={t('create_booking.customer_details.address')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={countryOptions}
                                    value={toCountryLabel(personalDetails?.country ?? '')}
                                    onChange={(label) => onCountryChange(toCountryValue(label))}
                                    disabled={isCanceled}
                                    label={
                                        t(
                                            'create_booking.customer_details.country',
                                        )
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onCityChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.city ?? ''}
                                    label={t('create_booking.customer_details.city')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onZipCodeChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.zipCode ?? ''}
                                    label={t('create_booking.customer_details.zip_code')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onCommentChange}
                                    disabled={isCanceled}
                                    value={comment}
                                    label={t(commentLabelKey(createdBy))}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onEmailNoteChange}
                                    disabled={isCanceled}
                                    value={emailNote ?? ''}
                                    maxLength={BOOKING_EMAIL_NOTE_MAX_LENGTH}
                                    label={t('create_booking.customer_details.email_note')}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />


                        {bookingFor === 'someone_else' && <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />}
                        {bookingFor === 'someone_else' && (
                            <Typography variant={'h3'} className={'mb-3'}>
                                {t('booking_details.guest')}
                            </Typography>
                        )}

                        {bookingFor === 'someone_else' && (
                            <Grid
                                container={true}
                                spacing={2}
                                className={'w-[100%] xl:w-[70%]'}
                                sx={{
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseFirstNameChange}
                                        disabled={isCanceled}
                                        value={someoneElseDetails?.firstName}
                                        label={t('create_booking.booking_for.first_name') + ' *'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseLastNameChange}
                                        disabled={isCanceled}
                                        value={someoneElseDetails?.lastName}
                                        label={t('create_booking.booking_for.last_name') + ' *'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseEmailChange}
                                        disabled={isCanceled}
                                        value={someoneElseDetails?.email}
                                        label={t('create_booking.booking_for.email') + ' *'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneNumberComponent
                                        enabled={!isCanceled}
                                        initialValue={someoneElseDetails?.phone}
                                        onChange={onSomeoneElsePhoneChange}
                                        valid={someoneElseDetails.phone.length > 1}
                                        required={true}
                                        label={t('create_booking.booking_for.phone')}
                                    />
                                </Grid>
                            </Grid>
                        )}
                        <div className={'flex space-x-2 mt-6'}>
                            <CustomButton title={t('edit_booking.button.cancel')} onClick={() => navigate(-1)} isFilled={false} />
                            <CustomButton
                                title={t('edit_booking.button.confirm_only')}
                                onClick={() => handleEditBooking('save_only')}
                                loading={loadingEditBookingOnlySave}
                                disabled={saveOnlyButtonDisabled}
                                isFilled={false}
                            />
                            <CustomButton
                                title={t(sendCopy.button)}
                                onClick={() => setOpen(true)}
                                isFilled={true}
                                disabled={saveButtonDisabled || !personalDataValidation.email || !!mailRefusal}
                            />
                        </div>
                        <CustomerMailBlockedNote refusal={mailRefusal} />
                        <SnackbarComponent
                            open={snackbarData.open}
                            message={snackbarData.message}
                            severity={snackbarData.severity}
                            onClose={handleCloseSnackbar}
                        />
                        <CustomModal
                            open={open}
                            onClose={() => setOpen(false)}
                            title={t(sendCopy.title)}
                            saveButton={
                                <CustomButton
                                    title={t('edit_booking.modal.confirm_button')}
                                    onClick={() => handleEditBooking('save_and_send_email')}
                                    disabled={saveButtonDisabled || !personalDataValidation.email || !!mailRefusal}
                                    loading={loadingEditBookingSaveAndSend}
                                    isFilled={true}
                                    fullWidth={false}
                                />
                            }
                            closeButton={<CustomButton title={t('edit_booking.modal.cancel_button')} onClick={() => setOpen(false)} isFilled={false} />}
                            description={t(sendCopy.description)}
                        ></CustomModal>
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default EditVenueBookingPage;
